import * as THREE from "three";
import {
  getStairGeometry,
  normalizeRailedEdges,
  type StairsConfig,
  type Vertex,
} from "../lib/geometry";

const POST_SPACING_FT = 4;

// Logo texture is shared across rebuilds — buildDeck runs on every prop
// change, so loading a fresh texture each time would re-decode the PNG.
let watermarkTexture: THREE.Texture | null = null;
function getWatermarkTexture(): THREE.Texture {
  if (!watermarkTexture) {
    watermarkTexture = new THREE.TextureLoader().load("/viewrail-icon.png");
    watermarkTexture.colorSpace = THREE.SRGBColorSpace;
    watermarkTexture.anisotropy = 4;
    // Plane is rotateX(-π/2) so its UV-V axis points toward the camera; with
    // the default flipY the logo lands upside-down in the default 3D view.
    watermarkTexture.flipY = false;
  }
  return watermarkTexture;
}

export type RailHeight = 36 | 42;
export type RailColor = "black" | "silver" | "white";

const CABLES_BY_HEIGHT: Record<RailHeight, number> = {
  36: 10,
  42: 12,
};

const COLORS: Record<RailColor, { post: number; cable: number }> = {
  black: { post: 0x1a1a1a, cable: 0xb8b8b8 },
  silver: { post: 0x9aa1a7, cable: 0xcfd3d7 },
  white: { post: 0xececec, cable: 0xc4c4c4 },
};

export type DeckConfig = {
  vertices: Vertex[];
  stairs: StairsConfig;
  railHeightInches: RailHeight;
  color: RailColor;
  railedEdges?: boolean[];
};

type Materials = {
  deck: THREE.MeshStandardMaterial;
  post: THREE.MeshStandardMaterial;
  cable: THREE.MeshStandardMaterial;
};

function makeMaterials(color: RailColor): Materials {
  const c = COLORS[color];
  return {
    deck: new THREE.MeshStandardMaterial({
      color: 0xb89770,
      roughness: 0.85,
      metalness: 0,
    }),
    post: new THREE.MeshStandardMaterial({
      color: c.post,
      metalness: 0.55,
      roughness: 0.4,
    }),
    cable: new THREE.MeshStandardMaterial({
      color: c.cable,
      metalness: 0.75,
      roughness: 0.3,
    }),
  };
}

export function buildDeck(content: THREE.Group, cfg: DeckConfig) {
  const { vertices, stairs, railHeightInches, color } = cfg;
  const railed = normalizeRailedEdges(cfg.railedEdges, vertices.length);
  const mats = makeMaterials(color);

  const stairsG = getStairGeometry(vertices, stairs);
  const stairEdgeRailed =
    stairs.edgeIndex < vertices.length ? railed[stairs.edgeIndex] : false;

  // Centroid for centering the deck in world space.
  const cx = vertices.reduce((s, p) => s + p.x, 0) / vertices.length;
  const cy = vertices.reduce((s, p) => s + p.y, 0) / vertices.length;

  const railHeightFt = railHeightInches / 12;
  const cableCount = CABLES_BY_HEIGHT[railHeightInches];

  // When stairs are present, lift the deck so the bottom step lands at ground.
  const stairDrop = stairsG ? Math.min(stairsG.stairLength * 0.6, 6) : 0;
  const deckBaseY = stairDrop;
  const deckThickness = 0.6;
  const slabDepth = deckThickness + deckBaseY;

  // Step layout: first riser drops off the deck, last step sits on the ground.
  // N treads + (N+1) risers = stairDrop, so each riser is stairDrop / (N+1).
  const stepCount = 5;
  const stepDrop = stairsG ? stairDrop / (stepCount + 1) : 0;
  const stairHorizDepth = stairsG ? Math.min(stairsG.stairLength, 12) : 0;
  const stepDepth = stairsG ? stairHorizDepth / stepCount : 0;

  // Deck slab — extruded centered polygon, rotated so polygon-y maps to world-z.
  const shape = new THREE.Shape();
  const cv = vertices.map((p) => ({ x: p.x - cx, y: p.y - cy }));
  shape.moveTo(cv[0].x, cv[0].y);
  for (let i = 1; i < cv.length; i++) shape.lineTo(cv[i].x, cv[i].y);
  shape.closePath();
  const deckGeom = new THREE.ExtrudeGeometry(shape, {
    depth: slabDepth,
    bevelEnabled: false,
  });
  deckGeom.rotateX(Math.PI / 2);
  const deck = new THREE.Mesh(deckGeom, mats.deck);
  deck.position.y = deckBaseY;
  content.add(deck);

  // Subtle Viewrail watermark "carved" into the deck top.
  const xs = vertices.map((p) => p.x);
  const ys = vertices.map((p) => p.y);
  const wmSpan = Math.min(
    Math.max(...xs) - Math.min(...xs),
    Math.max(...ys) - Math.min(...ys),
  );
  if (wmSpan > 0.5) {
    const wmW = wmSpan * 0.6;
    const wmH = wmW / (1189 / 255); // icon aspect ≈ 1189:255
    const watermark = new THREE.Mesh(
      new THREE.PlaneGeometry(wmW, wmH),
      new THREE.MeshBasicMaterial({
        map: getWatermarkTexture(),
        transparent: true,
        opacity: 0.18,
        depthWrite: false,
      }),
    );
    watermark.rotation.x = -Math.PI / 2;
    // Lift the watermark well above the slab — at orbit distance the depth
    // buffer can't distinguish anything finer, and an explicit renderOrder
    // keeps it drawn after the opaque deck.
    watermark.position.y = deckBaseY + 0.05;
    watermark.renderOrder = 1;
    content.add(watermark);
  }

  const postSize = 0.18;
  const postGeom = new THREE.BoxGeometry(postSize, railHeightFt, postSize);
  const cableGeom = new THREE.CylinderGeometry(0.028, 0.028, 1, 8, 1);
  const upVec = new THREE.Vector3(0, 1, 0);

  // Dark outline so light rail colors (white/silver) stay visible against
  // bright backgrounds. Two layers:
  //  - A back-face black shell scaled slightly larger than the mesh — its
  //    back faces poke past the colored surface to draw a thin halo.
  //  - Crisp edge lines on top to sharpen the silhouette.
  // WebGL ignores linewidth, so the shell does the visual heavy lifting.
  const edgeMat = new THREE.LineBasicMaterial({ color: 0x000000 });
  const shellMat = new THREE.MeshBasicMaterial({
    color: 0x000000,
    side: THREE.BackSide,
  });
  // Absolute halo — uniform scale would extend long rails by feet on each end.
  const haloOffset = 0.02;
  const addWithEdges = (mesh: THREE.Mesh) => {
    const geom = mesh.geometry;
    if (geom instanceof THREE.BoxGeometry) {
      const { width, height, depth } = geom.parameters;
      const shell = new THREE.Mesh(
        new THREE.BoxGeometry(
          width + haloOffset,
          height + haloOffset,
          depth + haloOffset,
        ),
        shellMat,
      );
      shell.position.copy(mesh.position);
      shell.quaternion.copy(mesh.quaternion);
      content.add(shell);
    }
    content.add(mesh);
    const edges = new THREE.LineSegments(
      new THREE.EdgesGeometry(geom),
      edgeMat,
    );
    edges.position.copy(mesh.position);
    edges.quaternion.copy(mesh.quaternion);
    edges.scale.copy(mesh.scale);
    content.add(edges);
  };

  const stairOpeningHalf = stairsG ? stairsG.stairWidth / 2 : 0;

  const buildEdgeRun = (
    wax: number,
    waz: number,
    wbx: number,
    wbz: number,
  ) => {
    const dx = wbx - wax;
    const dz = wbz - waz;
    const segLen = Math.hypot(dx, dz);
    if (segLen < 0.05) return;
    const midX = (wax + wbx) / 2;
    const midZ = (waz + wbz) / 2;
    const yaw = Math.atan2(dz, dx);
    const nPosts = Math.max(2, Math.ceil(segLen / POST_SPACING_FT) + 1);
    for (let j = 0; j < nPosts; j++) {
      const t = j / (nPosts - 1);
      const post = new THREE.Mesh(postGeom, mats.post);
      post.position.set(wax + dx * t, deckBaseY + railHeightFt / 2, waz + dz * t);
      addWithEdges(post);
    }
    const rail = new THREE.Mesh(
      new THREE.BoxGeometry(segLen + postSize, 0.14, 0.2),
      mats.post,
    );
    rail.position.set(midX, deckBaseY + railHeightFt + 0.07, midZ);
    rail.rotation.y = -yaw;
    addWithEdges(rail);

    const edgeDir = new THREE.Vector3(dx, 0, dz).normalize();
    const q = new THREE.Quaternion().setFromUnitVectors(upVec, edgeDir);
    const cableBottom = 0.3;
    const cableTop = railHeightFt - 0.35;
    for (let c = 0; c < cableCount; c++) {
      const t = c / (cableCount - 1);
      const cable = new THREE.Mesh(cableGeom, mats.cable);
      cable.scale.y = segLen;
      cable.quaternion.copy(q);
      cable.position.set(
        midX,
        deckBaseY + cableBottom + t * (cableTop - cableBottom),
        midZ,
      );
      content.add(cable);
    }
  };

  // Convert centered CAD point to world (x, z)
  const toWorld = (p: Vertex) => ({ x: p.x - cx, z: p.y - cy });

  for (let i = 0; i < vertices.length; i++) {
    if (!railed[i]) continue;
    const a = vertices[i];
    const b = vertices[(i + 1) % vertices.length];
    const wa = toWorld(a);
    const wb = toWorld(b);

    if (
      stairsG &&
      stairs.withRailing &&
      stairsG.edgeIndex === i &&
      stairOpeningHalf * 2 < stairsG.edgeLen - 0.05
    ) {
      const dxw = wb.x - wa.x;
      const dzw = wb.z - wa.z;
      const segLen = Math.hypot(dxw, dzw);
      const tx = dxw / segLen;
      const tz = dzw / segLen;
      const midX = (wa.x + wb.x) / 2;
      const midZ = (wa.z + wb.z) / 2;
      const gapL = {
        x: midX - tx * stairOpeningHalf,
        z: midZ - tz * stairOpeningHalf,
      };
      const gapR = {
        x: midX + tx * stairOpeningHalf,
        z: midZ + tz * stairOpeningHalf,
      };
      buildEdgeRun(wa.x, wa.z, gapL.x, gapL.z);
      buildEdgeRun(gapR.x, gapR.z, wb.x, wb.z);
    } else {
      buildEdgeRun(wa.x, wa.z, wb.x, wb.z);
    }
  }

  // Stair side rails — slope down each side of the steps. Only when the
  // host edge actually carries railing on the deck side.
  if (stairsG && stairs.withRailing && stairEdgeRailed) {
    const txW = stairsG.tx;
    const tzW = stairsG.ty;
    const oxW = stairsG.ox;
    const ozW = stairsG.oy;
    const half = stairsG.stairWidth / 2;
    const taper = Math.min(half * 0.18, 0.4);
    const depth = stairHorizDepth;
    const sMidX = stairsG.midX - cx;
    const sMidZ = stairsG.midY - cy;

    const gapLX = sMidX - txW * half;
    const gapLZ = sMidZ - tzW * half;
    const gapRX = sMidX + txW * half;
    const gapRZ = sMidZ + tzW * half;
    // Stair railing is standalone — its top post stands right next to the
    // deck corner post (not connected). Offset outward so the two posts are
    // visually distinct rather than z-fighting.
    const topOffset = postSize;
    const topLX = gapLX + oxW * topOffset;
    const topLZ = gapLZ + ozW * topOffset;
    const topRX = gapRX + oxW * topOffset;
    const topRZ = gapRZ + ozW * topOffset;
    const botLX = gapLX + txW * taper + oxW * depth;
    const botLZ = gapLZ + tzW * taper + ozW * depth;
    const botRX = gapRX - txW * taper + oxW * depth;
    const botRZ = gapRZ - tzW * taper + ozW * depth;

    const xAxis = new THREE.Vector3(1, 0, 0);

    // Top post starts at the deck plane (same height as deck posts). Bottom
    // post sits on top of the last tread. Rail spans the full drop between.
    const topBaseY = deckBaseY;
    const botBaseY = stepDrop;
    const railDrop = topBaseY - botBaseY;

    const buildStairRailRun = (
      topX: number,
      topZ: number,
      botX: number,
      botZ: number,
    ) => {
      const dxh = botX - topX;
      const dzh = botZ - topZ;
      const horizLen = Math.hypot(dxh, dzh);
      if (horizLen < 0.05) return;
      const slopeLen = Math.hypot(horizLen, railDrop);
      const slopeDir = new THREE.Vector3(dxh, -railDrop, dzh).normalize();
      const qRail = new THREE.Quaternion().setFromUnitVectors(xAxis, slopeDir);
      const qCable = new THREE.Quaternion().setFromUnitVectors(upVec, slopeDir);

      const nPosts = Math.max(2, Math.ceil(horizLen / POST_SPACING_FT) + 1);
      for (let j = 0; j < nPosts; j++) {
        const t = j / (nPosts - 1);
        const px = topX + dxh * t;
        const pz = topZ + dzh * t;
        // Each post sits on top of a tread. Endpoints land on the first/last
        // tread; intermediates snap to whichever tread the post sits over.
        let baseY: number;
        if (j === 0) {
          baseY = topBaseY;
        } else if (j === nPosts - 1) {
          baseY = botBaseY;
        } else {
          const stepIdx = Math.min(stepCount - 1, Math.floor(t * stepCount));
          baseY = deckBaseY - (stepIdx + 1) * stepDrop;
        }
        const railY = topBaseY + railHeightFt - railDrop * t;
        const postH = Math.max(0.1, railY - baseY);
        const postMesh = new THREE.Mesh(
          new THREE.BoxGeometry(postSize, postH, postSize),
          mats.post,
        );
        postMesh.position.set(px, baseY + postH / 2, pz);
        addWithEdges(postMesh);
      }

      const midX = (topX + botX) / 2;
      const midZ = (topZ + botZ) / 2;
      const midBaseY = (topBaseY + botBaseY) / 2;

      const rail = new THREE.Mesh(
        new THREE.BoxGeometry(slopeLen + postSize, 0.14, 0.2),
        mats.post,
      );
      rail.position.set(midX, midBaseY + railHeightFt + 0.07, midZ);
      rail.quaternion.copy(qRail);
      addWithEdges(rail);

      const cableBottom = 0.3;
      const cableTop = railHeightFt - 0.35;
      for (let c = 0; c < cableCount; c++) {
        const t = c / (cableCount - 1);
        const cable = new THREE.Mesh(cableGeom, mats.cable);
        cable.scale.y = slopeLen;
        cable.quaternion.copy(qCable);
        cable.position.set(
          midX,
          midBaseY + cableBottom + t * (cableTop - cableBottom),
          midZ,
        );
        content.add(cable);
      }
    };

    buildStairRailRun(topLX, topLZ, botLX, botLZ);
    buildStairRailRun(topRX, topRZ, botRX, botRZ);
  }

  // Stair steps
  if (stairsG) {
    const stairW = stairsG.stairWidth;
    const stepGeom = new THREE.BoxGeometry(stairW, stepDrop, stepDepth);

    // Step direction: outward normal in world space (CAD y maps to world z).
    const oxW = stairsG.ox;
    const ozW = stairsG.oy;
    const yaw = Math.atan2(ozW, oxW);
    const sMidX = stairsG.midX - cx;
    const sMidZ = stairsG.midY - cy;

    for (let i = 0; i < stepCount; i++) {
      const distFromEdge = stepDepth / 2 + i * stepDepth;
      // Tread i top sits one extra riser below the deck (no step flush with deck).
      const stepCenterY = deckBaseY - stepDrop * (i + 1.5);
      const step = new THREE.Mesh(stepGeom, mats.deck);
      step.position.set(
        sMidX + oxW * distFromEdge,
        stepCenterY,
        sMidZ + ozW * distFromEdge,
      );
      step.rotation.y = -yaw + Math.PI / 2;
      content.add(step);
    }
  }
}
